import { useEffect, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import toast from "react-hot-toast";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { ITEM_CATEGORIES } from "../constants/itemCategories";
import {
  getAllTrademarks,
  createTrademark,
  updateTrademark,
  deleteTrademark,
} from "../api/trademarkApi";
import api from "../api";
import { getToken } from "../Auth/Token";

export default function ItemCategorySection({ product, setProduct }) {
  const [trademarkOptions, setTrademarkOptions] = useState([]);
  const [trademarksList, setTrademarksList] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newTrademark, setNewTrademark] = useState("");
  const [newTrademarkPrefix, setNewTrademarkPrefix] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingTmId, setEditingTmId] = useState(null);
  const [editingTmName, setEditingTmName] = useState("");
  const [editingTmPrefix, setEditingTmPrefix] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [prevType, setPrevType] = useState(product.type);

  useEffect(() => {
    fetchTrademarks();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (product.type === prevType) return;

    const regularCat = ITEM_CATEGORIES.find(
      (cat) => cat.en === "Spice Blends VRAC",
    );
    const customCat = ITEM_CATEGORIES.find(
      (cat) => cat.en === "Spice Blends Custom VRAC",
    );
    const ingredientCat = ITEM_CATEGORIES.find(
      (cat) => cat.en === "Spices Bulk VRAC",
    );

    if (product.type === "custom" && customCat) {
      setProduct((prev) => ({
        ...prev,
        itemCategory: customCat.en,
        categorie: customCat.fr,
      }));
    } else if (product.type === "ingredient" && ingredientCat) {
      setProduct((prev) => ({
        ...prev,
        itemCategory: ingredientCat.en,
        categorie: ingredientCat.fr,
      }));
    } else if (product.type === "regular" && regularCat) {
      setProduct((prev) => ({
        ...prev,
        itemCategory: regularCat.en,
        categorie: regularCat.fr,
      }));
    }

    setPrevType(product.type);
  }, [product.type, prevType, setProduct]);

  useEffect(() => {
    if (product.itemCategory) return;

    const regularCat = ITEM_CATEGORIES.find(
      (cat) => cat.en === "Spice Blends VRAC",
    );
    const customCat = ITEM_CATEGORIES.find(
      (cat) => cat.en === "Spice Blends Custom VRAC",
    );
    const ingredientCat = ITEM_CATEGORIES.find(
      (cat) => cat.en === "Spices Bulk VRAC",
    );

    const defaultCat =
      product.type === "custom"
        ? customCat
        : product.type === "ingredient"
          ? ingredientCat
          : regularCat;

    if (defaultCat) {
      setProduct((prev) => ({
        ...prev,
        itemCategory: defaultCat.en,
        categorie: defaultCat.fr,
      }));
    }
  }, [product.itemCategory, product.type, setProduct]);

  const fetchTrademarks = async () => {
    try {
      const data = await getAllTrademarks();
      setTrademarksList(data || []);

      const formatted = (data || []).map((t) => ({
        value: t.name,
        label: t.name,
      }));

      formatted.push({
        value: "__add_new__",
        label: "+ Add / Manage Trademarks",
      });

      setTrademarkOptions(formatted);
    } catch (err) {
      toast.error("Failed to load trademarks");
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = getToken();
      const res = await api.get("/api/customers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && Array.isArray(res.data.data)) {
        const opts = res.data.data.map((c) => ({
          value: c.customerName || c.key,
          label: c.customerName || c.key,
          key: c.key,
        }));
        setCustomerOptions(opts);
      }
    } catch (err) {
      console.warn("Failed to load customer list:", err.message);
    }
  };

  const closeTrademarkModal = () => {
    setShowModal(false);
    setNewTrademark("");
    setNewTrademarkPrefix("");
    setEditingTmId(null);
    setEditingTmName("");
    setEditingTmPrefix("");
  };

  const handleTrademarkChange = (option) => {
    if (!option) return;

    if (option.value === "__add_new__") {
      setNewTrademark("");
      setNewTrademarkPrefix("");
      setShowModal(true);
      return;
    }

    const matched = trademarksList.find((t) => t.name === option.value);

    setProduct((prev) => ({
      ...prev,
      trademark: option.value,
      customPrefix: matched?.customPrefix || prev.customPrefix || "",
    }));
  };

  const handleSaveTrademark = async () => {
    if (!newTrademark.trim()) {
      toast.error("Trademark name required");
      return;
    }

    if (newTrademarkPrefix.trim() && !/^\d{4}$/.test(newTrademarkPrefix.trim())) {
      toast.error("Custom prefix must be a 4-digit number (e.g. 1538)");
      return;
    }

    try {
      setSaving(true);

      const created = await createTrademark({
        name: newTrademark.trim(),
        customPrefix: newTrademarkPrefix.trim() || undefined,
      });

      // refresh list
      await fetchTrademarks();

      // auto select
      if (created?.name) {
        setProduct((prev) => ({
          ...prev,
          trademark: created.name,
          customPrefix: created.customPrefix || prev.customPrefix,
        }));
      }

      setNewTrademark("");
      setNewTrademarkPrefix("");
      toast.success("Trademark added");
    } catch (err) {
      toast.error(err.message || "Failed to create trademark");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (tm) => {
    setEditingTmId(tm._id || tm.id);
    setEditingTmName(tm.name || "");
    setEditingTmPrefix(tm.customPrefix || "");
  };

  const handleCancelEdit = () => {
    setEditingTmId(null);
    setEditingTmName("");
    setEditingTmPrefix("");
  };

  const handleUpdateTrademark = async (id) => {
    if (!editingTmName.trim()) {
      toast.error("Trademark name cannot be empty");
      return;
    }

    if (editingTmPrefix.trim() && !/^\d{4}$/.test(editingTmPrefix.trim())) {
      toast.error("Custom prefix must be a 4-digit number (e.g. 1538)");
      return;
    }

    try {
      setActionLoading(true);
      await updateTrademark(id, {
        name: editingTmName.trim(),
        customPrefix: editingTmPrefix.trim() || null,
      });
      toast.success("Trademark updated");
      if (product.trademark === editingTmName.trim() || product.trademark === editingTmId) {
        setProduct((prev) => ({
          ...prev,
          trademark: editingTmName.trim(),
          customPrefix: editingTmPrefix.trim() || prev.customPrefix,
        }));
      }
      setEditingTmId(null);
      setEditingTmName("");
      setEditingTmPrefix("");
      await fetchTrademarks();
    } catch (err) {
      toast.error(err.message || "Failed to update trademark");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTrademark = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove trademark "${name}"?`)) return;
    try {
      setActionLoading(true);
      await deleteTrademark(id);
      toast.success("Trademark removed");
      if (product.trademark === name) {
        setProduct((prev) => ({ ...prev, trademark: "A Spice Affair" }));
      }
      await fetchTrademarks();
    } catch (err) {
      toast.error(err.message || "Failed to delete trademark");
    } finally {
      setActionLoading(false);
    }
  };

  const currentCategory = product.itemCategory;
  const categoryOptions = ITEM_CATEGORIES
    .filter((cat) =>
      product.type === "ingredient"
        ? cat.en === "Spices Bulk VRAC"
        : cat.en !== "Spices Bulk VRAC",
    )
    .map((cat) => ({
      value: cat.en,
      label: cat.en,
    }));

  const selectedCategory =
    categoryOptions.find((opt) => opt.value === currentCategory) || null;

  const taxCategoryOptions = [
    { value: "Tax exemption", label: "Tax exemption" },
    { value: "Default", label: "Default" },
  ];
  const selectedTaxCategoryValue = product.taxCategory || "Default";
  const selectedTaxCategory =
    taxCategoryOptions.find((opt) => opt.value === selectedTaxCategoryValue) ||
    null;

  const selectedTrademark =
    trademarkOptions.find((opt) => opt.value === product.trademark) ||
    (product.trademark === "" || !product.trademark
      ? { value: "A Spice Affair", label: "A Spice Affair (default)" }
      : null);

  const currentTmDoc = trademarksList.find(
    (t) => t.name?.toUpperCase() === String(product.trademark || "").toUpperCase()
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6 md:p-8 min-w-0 w-full max-w-full overflow-hidden">
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4 sm:mb-6">ITEM CATEGORY</h2>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${product.type === "custom" ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4 sm:gap-6`}>
        {/* Tax Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tax Category
          </label>
          <Select
            options={taxCategoryOptions}
            value={selectedTaxCategory}
            onChange={(option) =>
              setProduct({
                ...product,
                taxCategory: option ? option.value : "",
              })
            }
            placeholder="Select tax category..."
            classNamePrefix="react-select"
          />
        </div>

        {/* Item Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Item Category
          </label>
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={(option) => {
              const newCategoryEn = option ? option.value : "";
              const matchedCategory = ITEM_CATEGORIES.find(
                (cat) => cat.en === newCategoryEn,
              );

              setProduct((prev) => ({
                ...prev,
                itemCategory: newCategoryEn,
                categorie: matchedCategory?.fr || "",
              }));

              if (
                product.type !== "ingredient" &&
                newCategoryEn === "Spice Blends Custom VRAC"
              ) {
                setProduct((prev) => ({
                  ...prev,
                  type: "custom",
                }));
              } else if (
                product.type !== "ingredient" &&
                newCategoryEn === "Spice Blends VRAC"
              ) {
                setProduct((prev) => ({
                  ...prev,
                  type: "regular",
                }));
              }
            }}
            placeholder="Select Category..."
            isSearchable
            classNamePrefix="react-select"
          />
        </div>

        {/* Customer / Private Label Client - Only for Custom Recipes */}
        {product.type === "custom" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer / PL Client
            </label>
            <CreatableSelect
              isClearable
              options={customerOptions}
              value={
                product.customerName
                  ? { value: product.customerName, label: product.customerName }
                  : null
              }
              onChange={(selected) => {
                const custName = selected ? (selected.value || selected.label) : "";
                setProduct((prev) => ({
                  ...prev,
                  customerName: custName,
                }));
                const matchedTm = trademarksList.find(
                  (t) => t.name?.toUpperCase() === custName.toUpperCase()
                );
                if (matchedTm) {
                  setProduct((prev) => ({
                    ...prev,
                    trademark: matchedTm.name,
                    customPrefix: matchedTm.customPrefix || prev.customPrefix,
                  }));
                }
              }}
              placeholder="Select or enter customer..."
              classNamePrefix="react-select"
            />
          </div>
        )}

        {/* Trademark */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trademark / Brand
          </label>
          <Select
            options={trademarkOptions}
            value={selectedTrademark}
            onChange={handleTrademarkChange}
            placeholder="Select trademark (default: A Spice Affair)"
            isSearchable
            classNamePrefix="react-select"
          />
          {product.type === "custom" && (
            <div className="mt-2 text-xs">
              {currentTmDoc?.customPrefix ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono font-medium">
                  Prefix: <strong>{currentTmDoc.customPrefix}</strong>
                </span>
              ) : (
                <span className="text-gray-500 italic">
                  Prefix auto-allocated (1538+) on generate
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Manage Trademarks & Prefixes</h3>
              <button
                onClick={closeTrademarkModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Add New Section */}
            <div className="pt-4 pb-4 border-b border-gray-100">
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                Add New Trademark & Prefix
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTrademark}
                  onChange={(e) => setNewTrademark(e.target.value)}
                  placeholder="Brand name (e.g. ROB'S FAMOUS)"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-900"
                />
                <input
                  type="text"
                  maxLength={4}
                  value={newTrademarkPrefix}
                  onChange={(e) => setNewTrademarkPrefix(e.target.value.replace(/\D/g, ""))}
                  placeholder="Prefix (e.g. 1538)"
                  className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-900 font-mono"
                />
                <button
                  onClick={handleSaveTrademark}
                  disabled={saving}
                  className="px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
                >
                  {saving ? "Adding..." : "Add"}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Prefix must be 4 digits (e.g. 1536, 1537). Leave empty to auto-assign on SKU generation.
              </p>
            </div>

            {/* Existing Trademarks List */}
            <div className="flex-1 overflow-y-auto pt-4 min-h-[160px] max-h-[300px]">
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                Existing Trademarks ({trademarksList.length})
              </label>
              <div className="space-y-2">
                {trademarksList.map((tm) => {
                  const tmId = tm._id || tm.id;
                  const isEditing = editingTmId === tmId;

                  return (
                    <div
                      key={tmId}
                      className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition"
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-2 flex-1 mr-2">
                          <input
                            type="text"
                            value={editingTmName}
                            onChange={(e) => setEditingTmName(e.target.value)}
                            placeholder="Brand name"
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                            autoFocus
                          />
                          <input
                            type="text"
                            maxLength={4}
                            value={editingTmPrefix}
                            onChange={(e) => setEditingTmPrefix(e.target.value.replace(/\D/g, ""))}
                            placeholder="Prefix (e.g. 1537)"
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm bg-white font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => handleUpdateTrademark(tmId)}
                            disabled={actionLoading}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Save Rename & Prefix"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">
                              {tm.name}
                            </span>
                            {tm.customPrefix && (
                              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md font-mono">
                                Prefix: {tm.customPrefix}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleStartEdit(tm)}
                              disabled={actionLoading}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                              title="Edit Name & Prefix"
                            >
                              <Pencil size={15} />
                            </button>
                            {tm.name !== "A Spice Affair" && (
                              <button
                                type="button"
                                onClick={() => handleDeleteTrademark(tmId, tm.name)}
                                disabled={actionLoading}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                title="Remove Trademark"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end pt-4 mt-2 border-t border-gray-100">
              <button
                onClick={closeTrademarkModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
